/**
 * 将后端返回的时间字符串统一转换为本地时间（浏览器所在时区）显示。
 *
 * 处理规则：
 *   - 含 Z 后缀（UTC）：直接解析，toLocaleString 转本地时区
 *   - 含 ±HH:MM 偏移：直接解析，toLocaleString 转本地时区
 *   - 无任何时区信息：视为 UTC，追加 Z 后再解析转本地时区
 *
 * 这样无论后端返回何种格式，均统一以用户本地时区（中国 UTC+8）呈现。
 *
 * @param {string|null|undefined} value - 后端时间字符串
 * @returns {string} 格式化后的本地时间，无值时返回 '--'
 */
export const formatDateTime = (value) => {
  if (!value) return '--';
  const str = String(value).trim();
  // 判断是否已包含时区信息
  const hasTimezone = str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str);
  const d = new Date(hasTimezone ? str : str + 'Z');
  if (isNaN(d.getTime())) return str;
  return d.toLocaleString('zh-CN', { hour12: false });
};

/**
 * 将后端时间字符串解析为 Date 对象，供 DateTimePicker 等组件使用。
 * 同样将无时区信息的字符串视为 UTC 处理。
 *
 * @param {string|null|undefined} value
 * @returns {Date}
 */
export const parseDateTime = (value) => {
  if (!value) return new Date();
  const str = String(value).trim();
  const hasTimezone = str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str);
  const d = new Date(hasTimezone ? str : str + 'Z');
  return isNaN(d.getTime()) ? new Date() : d;
};

/**
 * 展示后端直接返回的本地时间字符串，不做任何时区换算。
 * 适用于后端已用当地时区（北京时 UTC+8）直接写入、不带时区后缀的字段，
 * 例如 balanceUpdatedAt。
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const formatDateTimeLocal = (value) => {
  if (!value) return '--';
  const str = String(value).trim();
  const match = str.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;
  return str;
};
